<template>
  <div class="min-h-screen bg-background text-foreground">

    <!-- Hero -->
    <section class="border-b border-border">
      <div class="container mx-auto max-w-5xl px-4 sm:px-6 py-12 md:py-16">
        <div class="flex flex-col items-start gap-3">
          <span class="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground tracking-wide uppercase">
            <span class="h-1.5 w-1.5 rounded-full bg-primary"></span> Career Resources
          </span>
          <h1 class="text-3xl md:text-5xl font-bold tracking-tight">The Applypa Blog</h1>
          <p class="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Tips, guides, and insights to help freelancers and remote workers land better opportunities and grow their careers — in one place.
          </p>
        </div>
      </div>
    </section>

    <!-- Sticky filter bar -->
    <div class="sticky top-14 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      <div class="container mx-auto max-w-5xl px-4 sm:px-6">
        <div class="flex items-center gap-3 py-3">
          <!-- Category chips (scrollable on mobile) -->
          <div class="flex-1 overflow-x-auto no-scrollbar">
            <div class="flex items-center gap-2 min-w-max">
              <button
                v-for="cat in categories"
                :key="cat.id"
                class="chip inline-flex items-center px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap"
                :class="activeCategory === cat.id
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-background text-muted-foreground border-border hover:text-foreground hover:bg-accent'"
                @click="setCategory(cat.id)"
              >{{ cat.label }}</button>
            </div>
          </div>
          <!-- Search (desktop) -->
          <div class="hidden md:flex relative flex-shrink-0 w-64">
            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search articles..."
              class="w-full h-9 pl-9 pr-3 text-sm bg-muted/50 border border-border rounded-md placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-background"
            />
          </div>
        </div>
        <!-- Mobile search -->
        <div class="md:hidden pb-3">
          <div class="relative">
            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search articles..."
              class="w-full h-9 pl-9 pr-3 text-sm bg-muted/50 border border-border rounded-md placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-background"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Main -->
    <main class="container mx-auto max-w-5xl px-4 sm:px-6 py-10 md:py-14">

      <!-- List heading + count -->
      <div class="flex items-end justify-between mb-6 pb-4 border-b border-border">
        <h3 class="text-lg font-semibold tracking-tight">{{ listHeading }}</h3>
        <span class="text-sm text-muted-foreground">{{ resultCount }}</span>
      </div>

      <!-- Posts grid -->
      <div v-if="paginatedPosts.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
        <article v-for="post in paginatedPosts" :key="post.slug" class="group flex flex-col">
          <router-link :to="`/blog/${post.slug}`" class="block aspect-[16/10] rounded-lg overflow-hidden mb-4" :class="post.cover ? '' : 'img-placeholder ' + (tints[post.category]?.tint || '')">
            <img v-if="post.cover" :src="post.cover" :alt="post.title" class="w-full h-full object-cover" />
            <span v-else class="label">cover · {{ post.slug }}</span>
          </router-link>
          <div class="flex items-center gap-2 mb-2">
            <span
              class="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              :class="tints[post.category]?.chipClass || 'border-border bg-muted text-muted-foreground'"
            >{{ categoryLabel(post.category) }}</span>
            <span class="text-xs text-muted-foreground">{{ post.readTime }} min read</span>
          </div>
          <h3 class="text-base font-semibold leading-snug mb-2 group-hover:text-primary transition-colors">
            <router-link :to="`/blog/${post.slug}`">{{ post.title }}</router-link>
          </h3>
          <p class="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-4">{{ post.excerpt }}</p>
          <div class="mt-auto flex items-center gap-2.5 text-xs">
            <div class="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">{{ post.authorInitials }}</div>
            <span class="font-medium text-foreground">{{ post.author }}</span>
            <span class="text-muted-foreground">·</span>
            <span class="text-muted-foreground">{{ post.date }}</span>
          </div>
        </article>
      </div>

      <!-- Empty state -->
      <div v-else class="text-center py-20">
        <div class="inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-4">
          <i class="fas fa-newspaper text-muted-foreground"></i>
        </div>
        <h4 class="text-base font-semibold mb-1">No articles found</h4>
        <p class="text-sm text-muted-foreground">Try a different category or search term.</p>
      </div>

      <!-- Pagination -->
      <nav v-if="totalPages > 1" class="mt-14 flex items-center justify-between border-t border-border pt-6">
        <button
          class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground rounded-md hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:pointer-events-none"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          <i class="fas fa-arrow-left text-xs"></i> Previous
        </button>
        <div class="flex items-center gap-1">
          <button
            v-for="page in visiblePages"
            :key="page"
            class="h-8 w-8 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors"
            :class="page === currentPage
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground'"
            @click="currentPage = page"
          >{{ page }}</button>
        </div>
        <button
          class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground rounded-md hover:bg-accent disabled:opacity-40 disabled:pointer-events-none"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          Next <i class="fas fa-arrow-right text-xs"></i>
        </button>
      </nav>

      <!-- Newsletter -->
      <section class="mt-20 rounded-2xl border border-border bg-muted/40 p-8 md:p-12">
        <div class="grid md:grid-cols-[1fr_auto] items-center gap-6">
          <div>
            <h3 class="text-xl font-bold tracking-tight mb-1">Get new articles in your inbox</h3>
            <p class="text-sm text-muted-foreground">Career tips and remote job opportunities for freelancers and remote workers — once a week, no spam.</p>
          </div>
          <form class="flex flex-col sm:flex-row gap-2 w-full md:w-auto" @submit.prevent="handleSubscribe">
            <input
              v-model="newsletterEmail"
              type="email"
              required
              placeholder="your@email.com"
              class="flex-1 sm:w-64 h-10 px-3 text-sm bg-background border border-border rounded-md placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button type="submit" class="inline-flex items-center justify-center gap-1.5 h-10 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors">
              Subscribe <i class="fas fa-arrow-right text-xs"></i>
            </button>
          </form>
        </div>
        <p v-if="subscribed" class="mt-4 text-sm text-emerald-600 font-medium">
          <i class="fas fa-check-circle"></i> Thanks! You're subscribed.
        </p>
      </section>

    </main>
  </div>
</template>

<script>
import { CATEGORIES, TINTS, POSTS } from '@/data/blogPosts.js';

const POSTS_PER_PAGE = 6;

export default {
  name: 'BlogPage',
  data() {
    return {
      activeCategory: 'all',
      searchQuery: '',
      currentPage: 1,
      newsletterEmail: '',
      subscribed: false,
      categories: CATEGORIES,
      tints: TINTS,
      posts: POSTS,
    };
  },
  computed: {
    filteredPosts() {
      let posts = this.posts;
      if (this.activeCategory !== 'all') {
        posts = posts.filter(p => p.category === this.activeCategory);
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        posts = posts.filter(p =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          this.categoryLabel(p.category).toLowerCase().includes(q)
        );
      }
      return posts;
    },
    totalPages() {
      return Math.ceil(this.filteredPosts.length / POSTS_PER_PAGE);
    },
    paginatedPosts() {
      const start = (this.currentPage - 1) * POSTS_PER_PAGE;
      return this.filteredPosts.slice(start, start + POSTS_PER_PAGE);
    },
    visiblePages() {
      return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    },
    listHeading() {
      if (this.activeCategory === 'all') return 'All articles';
      return this.categoryLabel(this.activeCategory);
    },
    resultCount() {
      const n = this.filteredPosts.length;
      return n === 1 ? '1 article' : `${n} articles`;
    },
  },
  watch: {
    activeCategory() { this.currentPage = 1; },
    searchQuery()     { this.currentPage = 1; },
  },
  methods: {
    setCategory(id) {
      this.activeCategory = id;
    },
    categoryLabel(id) {
      return this.categories.find(c => c.id === id)?.label || '';
    },
    handleSubscribe() {
      this.subscribed = true;
      this.newsletterEmail = '';
    },
  },
};
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

.chip { transition: all 0.15s ease; }
.chip:hover { transform: translateY(-1px); }

.img-placeholder {
  background-image: repeating-linear-gradient(
    135deg,
    hsl(220 14% 96%) 0,
    hsl(220 14% 96%) 8px,
    hsl(220 14% 92%) 8px,
    hsl(220 14% 92%) 16px
  );
  position: relative;
}
.img-placeholder.tint-blue    { background-image: repeating-linear-gradient(135deg, #eff6ff 0, #eff6ff 8px, #dbeafe 8px, #dbeafe 16px); }
.img-placeholder.tint-emerald { background-image: repeating-linear-gradient(135deg, #ecfdf5 0, #ecfdf5 8px, #d1fae5 8px, #d1fae5 16px); }
.img-placeholder.tint-violet  { background-image: repeating-linear-gradient(135deg, #f5f3ff 0, #f5f3ff 8px, #ede9fe 8px, #ede9fe 16px); }
.img-placeholder.tint-amber   { background-image: repeating-linear-gradient(135deg, #fffbeb 0, #fffbeb 8px, #fef3c7 8px, #fef3c7 16px); }
.img-placeholder.tint-rose    { background-image: repeating-linear-gradient(135deg, #fff1f2 0, #fff1f2 8px, #ffe4e6 8px, #ffe4e6 16px); }
.img-placeholder .label {
  position: absolute; top: 10px; left: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px; letter-spacing: 0.04em;
  color: hsl(var(--muted-foreground));
  background: rgba(255, 255, 255, 0.7);
  padding: 2px 6px; border-radius: 4px;
}

.line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
</style>
